import { useState, useEffect } from "react";
import { useSyncUser } from "./hooks/useSyncUser";
import { useDevAuth } from "./hooks/useDevAuth";
import { useAuthActions } from "@frontegg/react";
import Router from "./routes/routes";
import PortalTermsModal from "./components/common/PortalTermsModal";
import { TERMS_CONTENT } from "./pages/signup/termsContent";
import { getAccessToken } from "./lib/auth-token";
import { usePreviewSession } from "./contexts/PreviewSessionContext";

const DEV_AUTH_BYPASS = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === "true";
const API_URL = import.meta.env.PROD ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:3001');

async function generateHash(content: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function loadFingerprint(): Promise<string> {
  try {
    const FP = await import("@fingerprintjs/fingerprintjs");
    const agent = await FP.load();
    const result = await agent.get();
    return result.visitorId;
  } catch {
    return "unavailable";
  }
}

export default function App() {
  const previewCtx = usePreviewSession();
  const isPreview = !!previewCtx;

  if (isPreview) return <PreviewApp />;
  return <AuthenticatedApp />;
}

function PreviewApp() {
  if (window.location.pathname.startsWith("/preview/")) {
    return <Router />;
  }
  return <Router />;
}

function AuthenticatedApp() {
  useSyncUser();
  const { isAuthenticated, user } = useDevAuth();
  const { logout } = useAuthActions();

  const [deviceFingerprint, setDeviceFingerprint] = useState<string | null>(null);

  const [termsStatus, setTermsStatus] = useState<{
    loading: boolean;
    needsAcceptance: boolean;
    checked: boolean;
    error: string | null;
  }>({
    loading: true,
    needsAcceptance: false,
    checked: false,
    error: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    loadFingerprint().then(setDeviceFingerprint);
  }, []);

  useEffect(() => {
    if (deviceFingerprint === null) return;

    const checkTermsStatus = async () => {
      if (!isAuthenticated || !user?.email) {
        setTermsStatus({ loading: false, needsAcceptance: false, checked: true, error: null });
        return;
      }

      if (DEV_AUTH_BYPASS) {
        console.warn("DEV: Skipping terms check in development mode");
        setTermsStatus({ loading: false, needsAcceptance: false, checked: true, error: null });
        return;
      }

      try {
        const fpParam = deviceFingerprint && deviceFingerprint !== 'unavailable'
          ? `?fingerprint=${encodeURIComponent(deviceFingerprint)}`
          : '';
        const token = getAccessToken();
        const response = await fetch(`${API_URL}/api/terms-status/${encodeURIComponent(user.email)}${fpParam}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const data = await response.json();

        if (data.hasDeclined && !data.hasAccepted) {
          setTermsStatus({ loading: false, needsAcceptance: true, checked: true, error: null });
          return;
        }

        setTermsStatus({
          loading: false,
          needsAcceptance: !data.hasAccepted,
          checked: true,
          error: null,
        });
      } catch (error) {
        console.error("Error checking terms status:", error);
        setTermsStatus({
          loading: false,
          needsAcceptance: true,
          checked: true,
          error: "Unable to verify terms status. Please try again.",
        });
      }
    };

    if (isAuthenticated && user?.email) {
      checkTermsStatus();
    } else {
      setTermsStatus({ loading: false, needsAcceptance: false, checked: true, error: null });
    }
  }, [isAuthenticated, user?.email, deviceFingerprint]);

  const handleAcceptTerms = async () => {
    if (!user?.email) return;

    if (DEV_AUTH_BYPASS) {
      console.warn("DEV: Accepting terms in development mode (no API call)");
      setTermsStatus({ loading: false, needsAcceptance: false, checked: true, error: null });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const termsHash = await generateHash(TERMS_CONTENT);

      const token = getAccessToken();
      const response = await fetch(`${API_URL}/api/terms-accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          email: user.email,
          fronteggUserId: user.id,
          termsHash,
          termsVersion: "2.1",
          deviceFingerprint: deviceFingerprint || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      setTermsStatus({ loading: false, needsAcceptance: false, checked: true, error: null });
    } catch (error) {
      console.error("Error accepting terms:", error);
      setSubmitError("Failed to record acceptance. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeclineTerms = async () => {
    if (!user?.email) return;

    if (DEV_AUTH_BYPASS) {
      console.warn("DEV: Terms declined - would logout in production");
      setTermsStatus({ loading: false, needsAcceptance: false, checked: true, error: null });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const termsHash = await generateHash(TERMS_CONTENT);

      const declineToken = getAccessToken();
      const response = await fetch(`${API_URL}/api/terms-decline`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(declineToken ? { Authorization: `Bearer ${declineToken}` } : {}),
        },
        body: JSON.stringify({
          email: user.email,
          fronteggUserId: user.id,
          termsHash,
          termsVersion: "2.1",
          deviceFingerprint: deviceFingerprint || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      logout();
    } catch (error) {
      console.error("Error declining terms:", error);
      logout();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setTermsStatus({ loading: true, needsAcceptance: false, checked: false, error: null });
    setSubmitError(null);
  };

  if (termsStatus.loading || deviceFingerprint === null) {
    return (
      <div className="min-h-screen bg-[#05050F] flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <PortalTermsModal
        open={termsStatus.needsAcceptance && termsStatus.checked}
        onAccept={handleAcceptTerms}
        onDecline={handleDeclineTerms}
        isSubmitting={isSubmitting}
        error={submitError || termsStatus.error}
        onRetry={termsStatus.error ? handleRetry : undefined}
      />
      <Router />
    </>
  );
}
